import { App, Input } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getApiError,
    getOrderAmountListThunk,
    getUserTeamGetByManagerThunk,
} from "tm-shared/api";
import { buildRoutePath, formatUAHStandard } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { Meta } from "@/shared/seo";
import { BlockLoader } from "@/shared/ui/BlockLoader";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import {
    setCurrentRoute,
    setSearchAgent,
} from "@/store/slices/manager/manager.slice";

import {
    StyledScrollContent,
    StyledSVCInputWrapper,
    StyledSVCList,
    StyledSVCListItem,
    StyledSVCMainContainer,
} from "./Styled";
import { TotalAccordion } from "./TotalAccordion";

function SVCheckout() {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const {
        user: { id, employee },
        manager: {
            checkoutAgentsList,
            checkoutTotals,
            searchAgent,
            lastUpdated,
        },
        offline: { offlineMode },
    } = useAppSelector((state) => state);

    const [svLoading, setSvLoading] = useState(!offlineMode);

    const normalizedSearch = searchAgent.trim().toLowerCase();

    const filteredAgents = useMemo(() => {
        if (!normalizedSearch) return checkoutAgentsList ?? [];

        return (
            checkoutAgentsList?.filter(
                ({ userName }) =>
                    userName?.toLowerCase().includes(normalizedSearch),
            ) ?? []
        );
    }, [checkoutAgentsList, normalizedSearch]);

    const handleNavigateToCheckout = useCallback(
        async (routeId: string) => {
            dispatch(setCurrentRoute(routeId));
            navigate(buildRoutePath(ROUTES.SVCheckoutRoute, { routeId }));
        },
        [dispatch, navigate],
    );

    useEffect(() => {
        if (offlineMode) return;

        const getAmountList = async () => {
            try {
                setSvLoading(true);
                if (employee?.id) {
                    await dispatch(
                        getOrderAmountListThunk({
                            employeePositionId: employee?.id,
                        }),
                    ).unwrap();
                    return;
                }

                await dispatch(
                    getUserTeamGetByManagerThunk({ managerId: id }),
                ).unwrap();
            } catch (error) {
                const { displayMessage } = getApiError(error);

                notification.error({
                    title: "Помилка при завантаженні",
                    description: displayMessage,
                });

                console.error(error);
            } finally {
                setSvLoading(false);
            }
        };

        getAmountList();
    }, [notification, dispatch, id, offlineMode, employee?.id]);

    const isLoading = svLoading;

    const renderList = useCallback(() => {
        if (isLoading) return <BlockLoader />;

        if (filteredAgents.length === 0)
            return (
                <SecondaryText padding="32px" textAlign="center">
                    {normalizedSearch
                        ? "Нічого не знайдено"
                        : "Список порожній"}
                </SecondaryText>
            );

        return filteredAgents.map(({ routeId, userName, amountTotal }) => {
            return (
                <StyledSVCListItem
                    key={routeId}
                    onClick={() => handleNavigateToCheckout(routeId)}
                >
                    <PrimaryText>{userName}</PrimaryText>

                    <SecondaryText>
                        {formatUAHStandard(amountTotal)}
                    </SecondaryText>
                </StyledSVCListItem>
            );
        });
    }, [filteredAgents, handleNavigateToCheckout, isLoading, normalizedSearch]);

    return (
        <>
            <Meta title="Checkout" />
            <Header title="Єдиний кошик" titleLoading={isLoading} />

            <StyledSVCMainContainer>
                <TotalAccordion {...checkoutTotals} />

                <StyledScrollContent>
                    <StyledSVCInputWrapper>
                        <Input.Search
                            size="large"
                            placeholder="Введіть ім'я"
                            value={searchAgent}
                            onChange={(e) =>
                                dispatch(setSearchAgent(e.target.value))
                            }
                            allowClear
                        />
                    </StyledSVCInputWrapper>

                    {lastUpdated?.checkouts && (
                        <SecondaryText>
                            Останнє оновлення: {lastUpdated.checkouts}
                        </SecondaryText>
                    )}

                    <StyledSVCList>{renderList()}</StyledSVCList>
                </StyledScrollContent>
            </StyledSVCMainContainer>
        </>
    );
}

export default SVCheckout;
